/**
 * @file CounterIdField.tsx
 * @description Поле номера счётчика (phase19.16, партия Р3г, v0.19.62;
 * SB133; формат и тексты ошибок — shared `counterIdError`).
 *
 * Ввод живёт в поле и записывается в часть COUNTERS при уходе из поля или
 * по Enter, а не на каждый символ (недописанный «1234» — тоже верный номер,
 * его незачем сохранять). Тогда же номер приводится к виду без пробелов и
 * показывается так. Пусто — сервис выключен (`null`). Неверный номер в
 * черновик не пишется — сервер не получит 422 `site_counters_*_id_invalid`;
 * ошибка формата видна после ухода из поля, узнаваемый чужой формат (старый
 * пиксель ВКонтакте `VK-RTRG-…`) — сразу, с подсказкой, что ввести.
 */

import { COUNTER_SERVICES, type SiteCounterService } from "@chaiberi/shared";
import { useId, useState } from "react";

import { Input } from "../../ui/input";
import { Label } from "../../ui/label";
import { FieldHint } from "../../ui/settings-groups";
import { COUNTER_FIELD_TEXTS, parseCounterIdInput } from "../../../lib/site-view/counters-form";

interface Props {
  service: SiteCounterService;
  /** Номер в черновике; `null` — сервис выключен. */
  value: string | null;
  disabled?: boolean;
  /** Верный номер или `null` (очищено). */
  onValid: (id: string | null) => void;
}

export function CounterIdField({ service, value, disabled = false, onValid }: Props) {
  const id = useId();
  const texts = COUNTER_FIELD_TEXTS[service];
  const [text, setText] = useState(value ?? "");
  const [touched, setTouched] = useState(false);
  const parsed = parseCounterIdInput(service, text);
  // Значение сменилось снаружи (перечитали черновик, отменили правки) — показать его, если своё верное.
  const [seen, setSeen] = useState(value);
  if (seen !== value) {
    setSeen(value);
    if (parsed.ok) setText(value ?? "");
  }
  const hintMessage = COUNTER_SERVICES[service].idHint?.message;
  const error = parsed.ok ? null : parsed.error;
  const shownError = error !== null && (touched || error === hintMessage) ? error : null;

  /** Уход из поля или Enter: показать номер без пробелов и записать, если он верный и другой. */
  function commit() {
    setTouched(true);
    if (!parsed.ok) return;
    setText(parsed.id ?? "");
    if (parsed.id !== value) onValid(parsed.id);
  }

  return (
    <div className="flex min-w-0 flex-col gap-1">
      <Label htmlFor={id}>{texts.label}</Label>
      <Input
        id={id}
        value={text}
        inputMode="numeric"
        autoComplete="off"
        spellCheck={false}
        enterKeyHint="done"
        placeholder={texts.placeholder}
        disabled={disabled}
        aria-invalid={shownError !== null}
        aria-describedby={`${id}-hint`}
        onChange={(e) => setText(e.target.value)}
        onBlur={commit}
        onKeyDown={(e) => (e.key === "Enter" ? commit() : undefined)}
      />
      {shownError ? (
        <p id={`${id}-hint`} role="alert" className="type-caption text-destructive">
          {shownError} Пока номер неверный, в черновике остаётся прежний.
        </p>
      ) : (
        <FieldHint id={`${id}-hint`}>{texts.hint}</FieldHint>
      )}
    </div>
  );
}
